import { Moment } from 'moment-timezone';
import { getMarketplaceDeposit } from 'reducers/marketplace/MarketplaceSelectors';
import { createSelector } from 'reselect';
import { RootState } from 'store';
import { MarketplaceDeposit, MarketplaceDepositCategory } from 'types/Marketplace';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { getServiceDurationByPet, getServicePriceByPet } from 'utils/serviceUtils';

export const getOnlineBookingState = (state: RootState) => state.onlineBooking;

export const getOnlineBookingStep = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.step
);

export const getOnlineBookingLoading = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.loading
);

export const getOnlineBookingSelectedStaffId = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.selectedStaffId
);

export const getOnlineBookingStaffs = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.staffs
);

export const getOnlineBookingAvailableStaffs = createSelector(
    getOnlineBookingState,
    ({ bookingItems, staffs }) =>
        staffs.filter(item =>
            bookingItems.every(bookingItem => bookingItem.service?.staffIds.includes(item.staff.id))
        )
);

export const getOnlineBookingEarliestNextAvailableDate = createSelector(
    getOnlineBookingState,
    ({ staffs }) => {
        return staffs.reduce<null | Moment>((date, item) => {
            if (!item.nextAvailableAt) {
                return date;
            }

            if (date === null) {
                date = item.nextAvailableAt.date;
            }

            if (item.nextAvailableAt.date.isBefore(date)) {
                date = item.nextAvailableAt.date;
            }

            return date;
        }, null);
    }
);

export const getOnlineBookingAvailability = createSelector(
    getOnlineBookingState,
    ({ availability, bookingItems, customer, selectedStaffId }) => {
        let hoursBySelectedStaff = availability;

        if (selectedStaffId) {
            hoursBySelectedStaff = hoursBySelectedStaff.filter(hour =>
                hour.staffAvailable.includes(selectedStaffId)
            );
        }

        // If no staff is selected, leave only hours by staffs that can perform selected services
        if (!selectedStaffId) {
            hoursBySelectedStaff = hoursBySelectedStaff.filter(hour =>
                bookingItems.some(
                    bookingItem =>
                        bookingItem.service?.staffIds?.some(staffId =>
                            hour.staffAvailable.includes(staffId)
                        )
                )
            );
        }

        if (customer.blocked) {
            hoursBySelectedStaff = [];
        }

        return {
            availableHours: hoursBySelectedStaff,
            morningHours: hoursBySelectedStaff.filter(hour => hour.datetime.hour() < 12),
            afternoonHours: hoursBySelectedStaff.filter(hour => hour.datetime.hour() >= 12)
        };
    }
);

export const getOnlineBookingItems = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.bookingItems
);

export const getOnlineBookingPets = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.pets
);

export const getOnlineBookingDate = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.date
);

export const getOnlineBookingTime = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.time
);

export const getOnlineBookingAgreementSignatures = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.agreementSignatures
);

export const getOnlineBookingCustomer = createSelector([getOnlineBookingState], onlineBooking => ({
    customer: onlineBooking.customer,
    isReturningClient: onlineBooking.isReturningClient
}));

export const getOnlineBookingBackEnabled = createSelector(
    getOnlineBookingState,
    ({ step, isReturningClient }) => {
        const disabledSteps = [
            OnlineBookingStep.Home,
            OnlineBookingStep.Payment,
            OnlineBookingStep.Success
        ];

        if (disabledSteps.includes(step)) return false;

        if (step === OnlineBookingStep.SelectPets && isReturningClient) return false;
        if (step === OnlineBookingStep.AddPet && isReturningClient) return false;

        return true;
    }
);

export const getOnlineBookingTotalDuration = createSelector([getOnlineBookingItems], items =>
    items.reduce((durationSum, bookingItem) => {
        const serviceDurationForPet = getServiceDurationByPet(
            bookingItem.service!,
            bookingItem.pet.type,
            bookingItem.pet.size
        );

        if (serviceDurationForPet) {
            durationSum += serviceDurationForPet;
        }

        return durationSum;
    }, 0)
);

export const getOnlineBookingTotalAmount = createSelector(
    [getMarketplaceDeposit, getOnlineBookingItems],
    (deposit, bookingItems) => {
        return bookingItems.reduce((totalAmount, bookingItem) => {
            let bookingItemTotalAmount = 0;

            if (bookingItem.service) {
                bookingItemTotalAmount += getServicePriceByPet(
                    bookingItem.service,
                    bookingItem.pet
                );
            }

            if (bookingItem.addons.length > 0) {
                bookingItemTotalAmount += bookingItem.addons.reduce((totalAmount, addon) => {
                    return totalAmount + addon.price;
                }, 0);
            }

            return totalAmount + bookingItemTotalAmount;
        }, 0);
    }
);

const calculateBookingItemPrice = (price: number, deposit?: MarketplaceDeposit) => {
    if (!deposit) {
        return 0;
    }

    if (deposit.category === MarketplaceDepositCategory.PERCENTAGE) {
        return (price * deposit.value) / 100;
    }

    return deposit.value;
};

export const getOnlineBookingTotalDeposit = createSelector(
    [getMarketplaceDeposit, getOnlineBookingItems],
    (deposit, bookingItems) => {
        const isDepositEnabled = deposit !== undefined;

        if (!isDepositEnabled) {
            return 0;
        }

        return bookingItems.reduce((totalDeposit, bookingItem) => {
            if (bookingItem.service) {
                totalDeposit += calculateBookingItemPrice(
                    getServicePriceByPet(bookingItem.service, bookingItem.pet),
                    deposit
                );
            }

            if (bookingItem.addons.length > 0) {
                totalDeposit += bookingItem.addons.reduce((totalAmount, addon) => {
                    return totalAmount + calculateBookingItemPrice(addon.price, deposit);
                }, 0);
            }

            return totalDeposit;
        }, 0);
    }
);
